import { BaseOrganizationResponse } from "@/redux/organizations/types";
import { FieldMappingKeys } from "@/domains/datablocks/types";
import {
  AuditResponse,
  BaseData,
  BasePaginationQueryParams,
  BasePermissions,
  DateAuditResponse,
} from "@/redux/types";
import { RequestStatus } from "@/domains/evidence-requests/constants";
import {
  AuditUserResponse,
  BaseUserResponse,
  UserWithoutOrganizationResponse,
} from "@/redux/users/types";
import { UserInviteResponse } from "@/redux/invites/types";

type DataBlockTags = "DataBlocks" | "DataBlock";
type DataTemplateTags = "DataTemplates" | "DataTemplate";
type DataFileTags = "DataFiles" | "DataFile";
type DataFileExtensionTags = "DataFileExtensions" | "DataFileExtension";
type DataBlockVersionTags = "DataBlockVersion" | "DataBlockVersions";
type DataBlockHistoryTags = "DataBlockHistory" | "DataBlockHistories";
type ChoiceOptionReferencesTags = "ChoiceOption" | "ChoiceOptions";
type DataBlockRequestsTags = "DataBlockRequest" | "DataBlockRequests";
type EvidenceAnalyticsTags = "EvidenceAnalytics";
export type Tags =
  | DataBlockTags
  | DataTemplateTags
  | DataFileTags
  | DataFileExtensionTags
  | DataBlockVersionTags
  | ChoiceOptionReferencesTags
  | DataBlockRequestsTags
  | EvidenceAnalyticsTags
  | DataBlockHistoryTags;

export type TagType =
  | { type: "DataBlocks" }
  | { type: "DataBlock"; id: number }
  | { type: "DataTemplates" }
  | { type: "DataTemplates"; id?: number }
  | { type: "DataTemplate"; id: number | "PARTIAL-LIST" }
  | { type: "DataFiles" }
  | { type: "DataFiles"; id?: number }
  | { type: "DataFile"; id: number | "PARTIAL-LIST" }
  | { type: "DataFileExtensions" }
  | { type: "DataFileExtensions"; id?: string }
  | { type: "DataFileExtension"; id: string | "PARTIAL-LIST" }
  | { type: "DataBlockVersion"; id?: number }
  | { type: "DataBlockVersions" }
  | { type: "DataBlockHistory"; id?: number }
  | { type: "DataBlockHistories" }
  | { type: "ChoiceOption"; id?: number }
  | { type: "ChoiceOptions" }
  | { type: "DataBlockRequests" }
  | { type: "DataBlockRequest"; id?: number };

export enum FieldType {
  TEXT = "TEXT",
  NUMBER = "NUMBER",
  DATE = "DATE",
  SELECT = "SELECT",
}

export enum DisplayType {
  INPUT = "INPUT",
  TEXT_AREA = "TEXT_AREA",
  RICH_TEXT = "RICH_TEXT",
  CHECKBOX = "CHECKBOX",
  DROPDOWN = "DROPDOWN",
  DROPDOWN_WITH_SCALE = "DROPDOWN_WITH_SCALE",
  SLIDER = "SLIDER",
  DATE_PICKER = "DATE_PICKER",
  DATE_RANGE_PICKER = "DATE_RANGE_PICKER",
}

export enum FormatType {
  LINK = "LINK",
}

export enum ValidationRuleTypes {
  REQUIRED = "REQUIRED",
  EITHER_REQUIRED = "EITHER_REQUIRED",
  DEPENDENT_REQUIRED = "DEPENDENT_REQUIRED",
  MIN_LENGTH = "MIN_LENGTH",
  MAX_LENGTH = "MAX_LENGTH",
  ALLOWED_FILE_TYPES = "ALLOWED_FILE_TYPES",
  MAX_FILE_SIZE = "MAX_FILE_SIZE",
  REGEX = "REGEX",
}

export enum ContentType {
  Options = "options",
  Users = "users",
  Files = "files",
  Subjects = "subjects",
  Chapters = "chapters",
  Languages = "languages",
  Countries = "countries",
}

export type SelectedOptionResponse = {
  choice: number;
  scale: number;
  disabled: boolean;
};

export type DynamicValueResponse =
  | string
  | number
  | number[]
  | string[]
  | (number | string | SelectedOptionResponse)[]
  | boolean
  | SelectedOptionResponse
  | SelectedOptionResponse[]
  | null
  | undefined;

export interface FieldValueResponse {
  id: number;
  dataBlock: number;
  field: number;
  value: DynamicValueResponse;
}

export interface ChoiceOptionResponse {
  id: number;
  label: string;
  order?: number;
  disabled: boolean;
  category: string;
  relatedOptions: ChoiceOptionResponse[];
}

export interface OptionConstraintResponse {
  id: number;
  affectsMainOptions: boolean;
  parentField: number;
  parentOption: number;
  allowedChildOptions: number[];
}

export interface DataFieldBaseResponse extends BaseData {
  title: string;
  type: FieldType;
  displayType?: DisplayType;
  formatType?: FormatType;
  multiple?: boolean;
  allowsCustomOptions: boolean;
  disabled: boolean;
  contentType?: ContentType;
}

export interface DataFieldResponse extends DataFieldBaseResponse {
  order: number;
  guide?: string;
  sliderHelpText?: string;
  helpText?: string;
  placeholder?: string;
  description: string | null;
  options: ChoiceOptionResponse[];
  constraints?: OptionConstraintResponse[];
  rules?: ValidationRuleResponse[];
}

export interface ValidationRuleResponse {
  id: number;
  type: ValidationRuleTypes;
  ruleFields: number[];
  value?: DynamicValueResponse;
  errorMessage: string;
  dependentOn: {
    value: Record<string, string | number | string[] | number[]>;
  } | null;
  options?: {
    alwaysVisible?: boolean;
  };
}

export interface BaseDataTemplateResponse {
  id: number;
  title: string;
  color?: string;
}

export interface DataTemplateResponse extends BaseDataTemplateResponse {
  shortDescription: string;
  fullDescription: string;
  disabled: boolean;
  estimatedCompletionTime: number | null;
  templateFields: DataFieldResponse[];
  fieldsMapping: Record<FieldMappingKeys, number>;
  validationRules: ValidationRuleResponse[];
  icon?: string;
}

export interface DataTemplatesQueryParams extends BasePaginationQueryParams {
  disabled?: boolean;
  isEvidence?: boolean;
}

export enum ChoiceOptionKind {
  SDG = "SDG",
  SDG_ALL = "SDG_ALL",
  SDG_LIGHT_REFERENCE = "SDG_LIGHT_REFERENCE",
  SDG_NONE = "SDG_NONE",
}

export interface ChoiceOptionsQueryParams extends BasePaginationQueryParams {
  ids?: number[];
  kinds?: string[];
  distinctByLabel?: boolean;
}

/**
 * Shape returned for a block that is only *attached* to something else
 */
export interface DataBlockSummaryResponse extends BaseData, DateAuditResponse {
  sharingRights: string;
  template: BaseDataTemplateResponse;
  fieldValues: FieldValueResponse[];
  fieldsMapping: Record<FieldMappingKeys, number>;
  createdBy: AuditUserResponse | null;
  updatedBy: AuditUserResponse | null;
}

export interface DataBlockListResponse
  extends BaseData,
    BasePermissions,
    AuditResponse {
  organization: BaseOrganizationResponse;
  template: BaseDataTemplateResponse;
  fieldValues: FieldValueResponse[];
  fieldsMapping: Record<FieldMappingKeys, number>;
  sharingRights: string;
  editable: boolean;
}

export interface BaseDataBlockRequest extends BaseData {
  status: keyof typeof RequestStatus;
  dataBlock?: number;
}

export type DataBlockRequestQueryParams = BasePaginationQueryParams;

export interface DataBlockRequest extends BaseDataBlockRequest {
  user?: BaseUserResponse;
  userInvite?: UserInviteResponse;
  dataTemplate: BaseDataTemplateResponse;
  message: string;
  expiresAt: string;
  createdBy: BaseUserResponse;
  dataBlock?: number;
}

export interface DataBlockResponse extends DataBlockListResponse {
  request?: BaseDataBlockRequest;
  isUsedInActiveSurvey: boolean;
  editable: boolean;
}

export type DataBlocksQueryParams = BasePaginationQueryParams & {
  isExample?: boolean;
  ids?: number | number[] | null;
  dataTemplates?: number | number[] | null;
  sharingRights?: string | string[] | null;
  contributors?: number | number[] | null;
  languages?: number | number[] | null;
  sections?: number | number[] | null;
  sdgs?: number | number[] | null;
  search?: string;
  user?: number;
  organizations?: number | number[] | null;
  excludeOrganizations?: number | number[] | null;
  prioritizeQuestion?: number;
  editable?: boolean;
  excludeCreators?: number | number[] | null;
};

export interface DataBlockReference {
  label: string;
  id?: number;
  surveyId?: number;
  discussionId?: number;
  uuid?: string;
}

export interface DataBlockReferencesResponse {
  surveys: DataBlockReference[];
  surveyViews: DataBlockReference[];
  discussions: DataBlockReference[];
  comments: DataBlockReference[];
  chapters: DataBlockReference[];
}

export interface DataBlockVersionResponse extends DataBlockListResponse {
  references: DataBlockReferencesResponse;
}

export interface DataBlockVersionsQueryParams
  extends BasePaginationQueryParams {
  id: number;
}

export interface DataBlockHistoryQueryParams extends BasePaginationQueryParams {
  userId?: number;
  startDate?: string;
  endDate?: string;
}

export enum ChangesetType {
  CREATION = "CREATION",
  UPDATE = "UPDATE",
}

export interface DataBlockChangesetResponse extends BaseData {
  changesetType: ChangesetType;
  createdBy: UserWithoutOrganizationResponse;
  createdAt: string;
  updatedAt: string;
  changes: FieldChangeResponse[];
}

export interface RelationChangeScaleItem {
  objectId: number;
  scale: number | null;
}

export interface FieldChangeResponse extends BaseData {
  dataField: DataFieldBaseResponse | null;
  fieldName: string;
  oldValue: {
    value: string | string[] | null;
  };
  newValue: {
    value: string | string[] | null;
  };
  oldRelations: number[] | RelationChangeScaleItem[] | null;
  newRelations: number[] | RelationChangeScaleItem[] | null;
}
